import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Bell, MessageCircle, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// Sprint 8.C — In-App-Banner für Conversational Trigger.
// Zeigt bis zu 5 pending Trigger als verschachtelte Cards mit Feedback-Buttons (post_bruecke)
// oder Primary-CTA (weekly_pattern / inactivity). Auto-Dismiss beim Klick auf X.

const RESPONSE_LABEL = {
  worked: "Hat funktioniert",
  partial: "Teilweise",
  difficult: "Nein, schwierig",
};

export default function TriggerBanner({ onChange }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // {triggerId: bool}

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/triggers");
      setItems(res.data?.items || []);
      if (onChange) onChange(res.data?.count || 0);
    } catch (_e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const respond = async (triggerId, response) => {
    setBusy((s) => ({ ...s, [triggerId]: true }));
    try {
      const r = await api.post(`/triggers/${triggerId}/respond`, { response });
      // Remove from list
      setItems((prev) => {
        const next = prev.filter((t) => t.id !== triggerId);
        if (onChange) onChange(next.length);
        return next;
      });
      const follow = r.data?.follow_up;
      if (follow?.suggest_new_bruecke) {
        // Optional: könnte hier Snackbar/Toast triggern. Zunächst nur Navigation-Link bereitstellen.
      }
    } catch (_e) { /* noop */ }
    finally {
      setBusy((s) => ({ ...s, [triggerId]: false }));
    }
  };

  const handleCta = (trigger) => {
    const cta = trigger.primary_cta;
    if (!cta) return;
    // Optimistic: mark as opened
    respond(trigger.id, "opened");
    if (cta.action === "new_bruecke") {
      navigate(cta.target || "/dashboard/anliegen");
      return;
    }
    if (cta.action === "scroll_to_focus" && cta.target) {
      const el = document.querySelector(`[data-testid="${cta.target}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.section
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        data-testid="trigger-banner"
        className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm"
      >
        <header className="flex items-center justify-between gap-3 mb-3">
          <h2 className="flex items-center gap-2 text-base font-extrabold tracking-tight text-[var(--fts-text-primary)]">
            <MessageCircle className="w-4 h-4 text-[#4563BB]" />
            Dein Flexpfad-Coach
            <span
              data-testid="trigger-banner-count"
              className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full bg-[#4563BB] text-white text-[10px] font-bold px-1.5"
            >
              {items.length}
            </span>
          </h2>
        </header>

        <ul className="space-y-3" data-testid="trigger-banner-list">
          {items.map((t) => (
            <li
              key={t.id}
              data-testid={`trigger-item-${t.id}`}
              className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 relative"
            >
              <button
                type="button"
                onClick={() => respond(t.id, "dismissed")}
                aria-label="Trigger schließen"
                data-testid={`trigger-dismiss-${t.id}`}
                className="absolute top-2 right-2 text-slate-400 hover:text-slate-700"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <p
                data-testid={`trigger-headline-${t.id}`}
                className="text-sm font-bold text-[var(--fts-text-primary)] pr-6"
              >
                <Bell className="inline w-3.5 h-3.5 text-[#4563BB] mr-1 -mt-0.5" />
                {t.headline}
              </p>
              {t.subtitle && (
                <p
                  data-testid={`trigger-subtitle-${t.id}`}
                  className="mt-1 text-xs text-slate-600 leading-relaxed"
                >
                  {t.subtitle}
                </p>
              )}

              {/* Feedback Buttons */}
              {Array.isArray(t.feedback_options) && t.feedback_options.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {t.feedback_options.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => respond(t.id, opt.key)}
                      disabled={!!busy[t.id]}
                      data-testid={`trigger-feedback-${t.id}-${opt.key}`}
                      className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${
                        opt.key === "worked"
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                          : opt.key === "partial"
                          ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                          : "bg-red-100 text-red-800 hover:bg-red-200"
                      }`}
                    >
                      {busy[t.id] && <Loader2 className="w-3 h-3 animate-spin" />}
                      {RESPONSE_LABEL[opt.key] || opt.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Primary CTA */}
              {t.primary_cta && (
                <button
                  type="button"
                  onClick={() => handleCta(t)}
                  disabled={!!busy[t.id]}
                  data-testid={`trigger-cta-${t.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[#4563BB] hover:bg-[#3a55a5] text-white px-3 py-1.5 text-xs font-bold disabled:opacity-50"
                >
                  {busy[t.id] && <Loader2 className="w-3 h-3 animate-spin" />}
                  {t.primary_cta.label}
                </button>
              )}
            </li>
          ))}
        </ul>
      </motion.section>
    </AnimatePresence>
  );
}
